import { Component, OnDestroy, OnInit, ViewChild, inject, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AppGlobals } from "src/app/app.globals";
import { EmailReportConfigItem } from "src/app/interfaces/entities/email-report";
import { ConfigService } from "src/app/services/config.service";
import { EmailReportRole } from "src/app/interfaces/enums/email-report-role";
import { EntraUser } from "src/app/interfaces/entities/user";
import {
  DxButtonModule,
  DxDataGridComponent,
  DxDataGridModule,
  DxDropDownBoxModule,
  DxListModule,
  DxTagBoxModule,
  DxToastModule,
} from "devextreme-angular";
import { DatabaseExportStatusConfig, DbExport } from "./database-exports.types";
import { DeletePopupComponent } from "../../../components/delete-popup/delete-popup.component";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { PillStatusConfig, StatusPillComponent } from "../../../components/status-pill/status-pill.component";

import { ActivityLogService } from "src/app/services/activity-log.service";
import { GlobalPopupService } from "src/app/components/generic-popup/presets/global-popup/global-popup.service";
import { ActivityEntity, ActivitySection } from "src/app/constants/activity-log";
import { ApiFileService } from "src/app/services/api-file.service";
@Component({
    selector: "app-database-export",
    imports: [
        CommonModule,
        DxButtonModule,
        DxDataGridModule,
        DxTagBoxModule,
        DxDropDownBoxModule,
        DxListModule,
        DxToastModule,
        DeletePopupComponent,
        StatusPillComponent,
        GpdButtonComponent,
        SvgIconComponent,
    ],
    templateUrl: "./database-export.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./database-export.component.css"]
})
export class DatabaseExportComponent implements OnInit, OnDestroy {
  private readonly apiFileService = inject(ApiFileService);

  dbExports: DbExport[] = [];

  exportConfigs: PillStatusConfig[];
  exportInProgress: boolean = true;

  selectedRows = [];

  private timer: any;

  popup: { message: string; onConfirm?: Function; onAbort?: Function } | null;

  constructor(
    private configService: ConfigService,
    private appGlobals: AppGlobals,
    private activityLog: ActivityLogService,
    private popupService: GlobalPopupService
  ) {}

  ngOnInit(): void {
    this.activityLog.logView({ Section: ActivitySection.CONFIG_DATABASE });
    this.exportConfigs = DatabaseExportStatusConfig;
    this.getPageData();
  }

  async getPageData() {
    try {
      const result = await this.configService.getDatabaseExportList();
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.dbExports = result.databaseExports;
      this.exportInProgress = this.dbExports.some(
        (x) => x.status == 2 || x.status == 3
      );
      this.startTimer();
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  /** Shown after "Delete manually": explains how to pick backups for deletion in the table. */
  showManualDeleteHint = false;

  /** Confirms an export in a neutral popup - app-delete-popup would title it "Delete". */
  exportDatabase() {
    const storageFull = this.dbExports.filter((x) => x.status == 0).length >= 10;
    this.popupService.showPopup({
      title: $localize`:@@exportDatabase:Export Database`,
      content: storageFull
        ? $localize`:@@exportDatabaseStorageWarning:The backup storage limit has been reached. Exporting will automatically delete the oldest backups. Would you like to delete a backup manually instead?`
        : $localize`:@@exportDatabaseWarning:Processing may take some time. You will receive an email notification once the process is complete.`,
      buttons: [
        { text: $localize`:@@cancel:Cancel`, type: "secondary", onClick: () => {} },
        ...(storageFull
          ? [
              {
                text: $localize`:@@deleteManually:Delete manually`,
                type: "secondary" as const,
                // The user picks the backups in the table (checkboxes) and deletes them there.
                onClick: () => (this.showManualDeleteHint = true),
              },
            ]
          : []),
        {
          text: storageFull ? $localize`:@@exportAnyway:Export anyway` : $localize`:@@export:Export`,
          type: "primary",
          onClick: () => this.startExport(),
        },
      ],
    });
  }

  private async startExport() {
    try {
      const result = await this.configService.exportDatabase();
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.getPageData();
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  async downloadExport(dbExport: DbExport) {
    try {
      await this.apiFileService.downloadDatabaseExport(dbExport.id);
      this.activityLog.logView({
        Section: ActivitySection.CONFIG_DATABASE,
        EntityType: ActivityEntity.DATABASE,
        EntityId: dbExport.id,
      });
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }
  async deleteDatabase() {
    this.popup = {
      message: $localize`:@@deleteDatabaseWarning:Are you sure you want to delete the database export.`,
      onConfirm: async () => {
        try {
          const result = await this.configService.deleteDatabaseExport({
            databaseExportIds: this.selectedRows,
          });
          if (result.hasError) {
            AppGlobals.ShowErrorMessage(result.feedback);
            return;
          }
          this.showManualDeleteHint = false;
          this.getPageData();
        } catch (error) {
          AppGlobals.ShowErrorMessage(error);
        }
      },
    };
  }

  startTimer(): void {
    if (this.exportInProgress && !this.timer) {
      this.timer = setInterval(() => {
        if (this.exportInProgress) {
          this.getPageData();
        } else {
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 10000);
    }
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}
